import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@csa/web-foundation";
import type { Page } from "@csa/web-foundation";
import type { Approval } from "../../api/config-contracts";
import type {
  CreateDsarInput,
  DsarFilter,
  DsarRequest,
  ExecuteErasureResult,
  UpdateDsarStatusInput,
} from "../../api/dsar-contracts";

// KVKK DSAR register reads/writes against /v1/admin/dsar. No business logic in the
// browser: the API is the boundary and re-enforces dsar:read / dsar:write on every
// call. The 30-day statutory clock (due_at) is server-derived; the forward-legal
// status transitions are re-guarded server-side (this only offers the legal moves).

const DSAR = ["admin", "dsar"];
const APPROVALS = ["admin", "approvals"];

/** Build the list query string from the optional status / type filters + cursor. */
function listUrl(filter: DsarFilter, cursor: string | null): string {
  const params = new URLSearchParams();
  if (filter.status) params.set("status", filter.status);
  if (filter.requestType) params.set("type", filter.requestType);
  if (cursor) params.set("cursor", cursor);
  const qs = params.toString();
  return qs ? `/v1/admin/dsar?${qs}` : "/v1/admin/dsar";
}

/** GET /v1/admin/dsar — the org-scoped DSAR register (Page envelope, keyset cursor).
 *  dsar:read@all. Filters (status / requestType) go server-side via the query params;
 *  a filter change is a fresh query key → refetch. Keyset "daha fazla" pagination via
 *  nextCursor. */
export function useDsarList(filter: DsarFilter, enabled = true) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: [...DSAR, "list", filter.status ?? null, filter.requestType ?? null],
    enabled,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => api.get<Page<DsarRequest>>(listUrl(filter, pageParam)),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

/** POST /v1/admin/dsar — log a new DSAR request. dsar:write@all, audited. due_at is
 *  server-derived (received_at + 30 days). On success the list is invalidated so the
 *  new request appears. */
export function useCreateDsar() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDsarInput) => api.post<DsarRequest>("/v1/admin/dsar", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: DSAR });
    },
  });
}

/** PATCH /v1/admin/dsar/:id — advance the handling status (a forward-legal transition;
 *  a terminal state stamps fulfilled_at). dsar:write@all, audited. An illegal transition
 *  is a 409 the caller surfaces cleanly. On success, refetch the list. */
export function useUpdateDsarStatus(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateDsarStatusInput) =>
      api.patch<DsarRequest>(`/v1/admin/dsar/${id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: DSAR });
    },
  });
}

// ── The KVKK erasure lane (GAP-AUDIT-3 D1) ──────────────────────────────────
// POST /v1/admin/dsar/:id/execute-erasure is the ONLY path that actually runs
// the S3-1 crypto-shred. It was built in #227 and never consumed here, so the
// desk was closing erasures with a plain status PATCH while nothing was
// destroyed. These hooks bind the real endpoint plus its four-eyes cycle.

/** The caller's own approvals — so the requester sees "approved → ready to erase".
 *  Shared shape with the config surface's GA1 cycle; same endpoint, same client. */
export function useMyApprovals(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: [...APPROVALS, "mine"],
    enabled,
    queryFn: () => api.get<Approval[]>("/v1/approvals/mine"),
  });
}

/** POST /v1/approvals — open a dsr.erase approval bound to THIS DSAR id (SF1).
 *  The second person grants it from their approver inbox; the requester can never
 *  grant their own (server-enforced, approver ≠ requester). */
export function useRequestErasureApproval() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { action: string; resourceId: string }) =>
      api.post<Approval>("/v1/approvals", input),
    onSettled: () => void qc.invalidateQueries({ queryKey: APPROVALS }),
  });
}

/** POST /v1/admin/dsar/:id/execute-erasure — run the crypto-shred and flip the
 *  register to FULFILLED with the shred correlation id. Carries the APPROVED
 *  approval as x-approval-id; step-up (recent strong auth) is re-checked server
 *  side and surfaces as CSA-1007, four-eyes as CSA-1008. */
export function useExecuteErasure(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (approvalId: string) =>
      api.post<ExecuteErasureResult>(`/v1/admin/dsar/${id}/execute-erasure`, undefined, {
        approvalId,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: DSAR });
      void qc.invalidateQueries({ queryKey: APPROVALS });
    },
  });
}
